import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from "react";

import { useWorker } from '@koale/useworker';

import { Container, Row, Col, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment-timezone";
import Select from "react-select";
import SitesSelect from './SitesSelect';
import PresetForm from "./DataDownloadPreset";
import { StarBorder, EmojiEmotionsOutlined } from '@material-ui/icons';

import { PRODUCTIONCOLS } from "../../../util/columns";
import { useUser } from "../../../util/user";
import { useSite } from "../../../util/sites";
import { useSolarCalc } from "../../../util/calculations";
import axios from 'axios';
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import { delay } from '../../../util/promise';
import { useWatchStateChange } from "../../../util/watchStateChange";
import { maxDbTableCellCountsToFetch } from "../../../util/configs";

import { AdvancedCol } from "./AdvancedCol";
import { DownloadableTable } from "./DownloadableTable";
import { StandardCol } from "./StandardCol";
import Loader from "../../general/Loader";
import LoaderGraph from "../../general/LoaderGraph";

import "../../general/general-comp.css";
import "./style.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import "react-datepicker/dist/react-datepicker.css";
import "./DownloadContents_global.css";
import PresetEventEmitter, { presetEventNames } from "../../../util/presetEventEmitter";

import dataLabelNameHiddenFns from '../../../util/dataLabelNameHiddenFns';

import { oneNumericalColumnShift } from '../../../util/tableAxisShift';
import FormLabel from "./FormLabel";
import { Link } from "react-router-dom";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";

import { NonceProtectContext } from '../../../util/nonceProtect';

const todayDate = moment().toDate();

const displayDate = (_moment) => {
	_moment = _moment || moment();
	return _moment.format("MMMM DD, YYYY");
}
const displayDateTime = (_moment) => {
	_moment = _moment || moment();
	return _moment.format("MMMM DD, YYYY HH:mm");
}
const formatDate = (_moment) => {
	_moment = _moment || moment();
	return _moment.format("YYYY-MM-DD");
}
const formatYear = (_moment) => {
	_moment = _moment || moment();
	return _moment.format("YYYY");
}
const formatDateTime = (_moment) => {
	_moment = _moment || moment();
	return _moment.format("YYYY-MM-DD HH:mm");
}

const usefulTime = (datetime) => moment(datetime).format("YYYY-MM-DD HH:mm");
const date = (date) => moment(date).format("YYYY-MM-DD");
const month = (date) => moment(date).format("MMMM YYYY");

// splits a string into an array of substrings
const word = (s, k, d) => {
	const n = s.toLowerCase().split(k);
	return n[n.length - d];
};

// capitalizeFirstLetter
function cfl(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

const calc = useSolarCalc();

const formatTableData = (param) => {
	const tableData = param.data;
	const dataTypeId = param.dataTypeId;

	// eslint-disable-next-line no-undef
	const usefulTime = (datetime) => dayjs(datetime).format('YYYY-MM-DD HH:mm');
	// eslint-disable-next-line no-undef
	const date = (date) => dayjs(date).format('YYYY-MM-DD');
	// eslint-disable-next-line no-undef
	const month = (date) => dayjs(date).format('YYYY-MM');

	return tableData.map(row => {
		// format data without knowing data "key"
		for(let col in row) {
			// EDMI meter
			if(col === 'EDMI_meter') {
				row[col] = row[col] === 1 ? "Split Production" : "Non-Split Production";
			}
			// solo / multi site
			else if(col === 'solo') {
				row[col] = row[col] === 1 ? "Solo" : "Multi";
			}
			// water
			else if(col === 'water') {
				row[col] = row[col] === 1 ? "Yes" : "No";
			}
			// wind direction
			else if(col === 'wind_dir') {
				row[col] = row[col] === 1 ? "Yes" : "No";
			}
			// irradiation sum
			else if(col === 'irradiance_sum') {
				row[col] = (row[col]/6000).toFixed(3);
			}
			// irradiation ON
			else if(col === 'irradiance_ON') {
				row[col] = (row[col]/6000).toFixed(3);
			}
			// irradiation OFF
			else if(col === 'irradiance_OFF') {
				row[col] = (row[col]/6000).toFixed(3);
			}
			// date
			else if(col === 'dateData') {
				switch(dataTypeId) { // this function get called only for the dataType p10, daily_calc, monthly_calc
					case 1:
						row[col] = usefulTime(row[col]);
						break;
					case 2:
						row[col] = date(row[col]);
						break;
					case 3:
						row[col] = month(row[col]);
						break;
					default:
						break;
				}
			}
			// format timestamp string
			else if(typeof(row[col]) === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}[:.0-9Z]*/.test(row[col])) {
				row[col] = date(row[col]);
			}
			// format number with thousond separater (",")
			else if(!/(year|month|date|tax_id|zipcode)/gi.test(col) && /^[0-9]+(\.[0-9]+)?$/gi.test(row[col])) {
				row[col] = parseFloat(row[col]).toLocaleString('en-US', { maximumFractionDigits: 16 });
			}
		}
		return row;
	});
}

const countryStyle = {
	container: (provided) => ({
		...provided,
		fontSize: "1.5rem !important",
		maxWidth: "300px !important",
		minWidth: "150px !important",
		float: "none !important",
		display: "inline-block !important",
	}),
	valueContainer: () => ({
		display: "flex",
		flex: "1 1 0%",
		flexWrap: "wrap",
		maxHeight: "60px",
		overflowY: "auto",
		color: "black",
	}),
	menu: (provided) => ({
		...provided,
		color: "#000",
		backgroundColor: "#fff",
	}),
};

let dataTypeReqSource; // for canceling pending previous data type request

export const DownloadContents = () => {
	const isCountryListFetched = useRef(false);
	const [isContract, setIsContract] = useState(false);
	const [isSite, setIsSite] = useState(false);
	const [isDataType, setIsDataType] = useState(false);
	const [isStandard, setIsStandard] = useState(false);
	const [isAdvanced, setIsAdvanced] = useState(false);
	const [isTable, setIsTable] = useState(false);
	const [isReBuildTable, setIsReBuildTable] = useState(false);

	const [userAttr, setUserAttr] = useState({});
	const isGY = useMemo(() => userAttr.type === 1 ? 1 : 0, [userAttr]);

	const [listCountry, setListCountry] = useState([]);
	const [listContract, setListContract] = useState([]);
	const listContractRef = useRef([]);
	const selectedContractsRef = useRef([]);
	const selectByOptions = useMemo(() => {
		let options = [ {label: "GETTING DATA BY CLIENT(S)", value: "client"} ]
		if(isGY) { options = [...options, { label: "GETTING DATA BY CONTRACT(S)", value: "contract" }] }
		return options;
	}, [ isGY ]);

	const [selectedCountry, setSelectedCountry] = useState([]);
	const [selectedSelectBy, setSelectedSelectBy] = useState(null);
	const [selectedContracts, setSelectedContracts] = useState(null);
	const [selectedClient, setSelectedClient] = useState([]);
	const [selectedSites, setSelectedSites] = useState([]);
	const selectedSitesCountry = useMemo(() => {
		const countries = selectedSites.map(site => site.country);
		const uniqueCountries = [...new Set(countries)];
		if(uniqueCountries.length !== 1) { return "ALL" }
		return uniqueCountries[0];
	}, [selectedSites]);

	const isSelectedSelectByForClient = useRef(false);

	const [selectedDataType, setSelectedDataType] = useState(null);
	const [listType, setListType] = useState([]);
	const [isListTypeLoading, setIsListTypeLoading] = useState(false);

	const selectedSitesRef = useRef([]);
	const listTypeRef = useRef([]);
	const selectedDataTypeRef = useRef(null);

	const isAllSelectSelected = useMemo(() => (!!selectedSelectBy && typeof(selectedDataType?.id) === 'number' && !!selectedSites?.length), [selectedSelectBy, selectedDataType, selectedSites]);

	const [tableCol, setTableCol] = useState([]);
	const [tableData, setTableData] = useState([]);
	const [tableFoot, setTableFoot] = useState([]);
	const [tableFilename, setTableFilename] = useState([]);
	const [tableTitle, setTableTitle] = useState([]);
	const [fixedCols, setFixedCols] = useState({});
	const [isTableLoading, setTableLoading] = useState(true);

	const prodcols = useMemo(() => PRODUCTIONCOLS, []);

	const clientOptions = useSite()?.allSites_Select || [];
	
	// sites select
	const [siteSelectFilterBySiteState, setSiteSelectFilterBySiteState] = useState(null);
	const siteSelectFilterByClient = userAttr?.type === 1 /*type 1 -> GY user, type 0 -> client*/ ? null : parseInt(userAttr?.client_id);
	const siteSelectFilterBySite = siteSelectFilterBySiteState?.map(site => site.site_id) || ((!userAttr?.site_id || userAttr.site_id === 'all') ? null : userAttr.site_id.split(',').map(siteId => parseInt(siteId)));
	const siteSelectFilterByCountry = selectedCountry === 'ALL' ? null : selectedCountry;

	// PRESET
	const [selectedPreset, setSelectedPreset] = useState(null);
	const [currentFieldSelection, setCurrentFieldSelection] = useState({}); // { [dataTypeId]: { "STANDARD": [...], "ADVANCED": {...} }, siteIds: Array<Number>|null, dateRange: Array<String>|null }
	const [isCreatePreset, setIsCreatePreset] = useState(false);
	const [editingPreset, setEditingPreset] = useState(null);
	const isEditPreset = !!editingPreset;
	const selectedPresetOfType = selectedPreset && selectedPreset.config?.dataType === selectedDataType?.id && selectedPreset;

	const handlePresetChange = useCallback(preset => {
		setSelectedPreset(preset);
	}, []);

	const clearPreset = useCallback(() => {
		setSelectedPreset(null);
	}, []);

	const handleCurrentFieldSelectionChange = useCallback(sections => {
		let fieldSelection = currentFieldSelection;
		sections.forEach(({ section, data }) => {
			if(section !== 'STANDARD' && section !== 'ADVANCED' && section !== 'DATE_RANGE') { return }
			if(fieldSelection[selectedDataType.value]?.[section] === data) { return }
			fieldSelection = {
				...fieldSelection,
				[selectedDataType.value]: {
					...(fieldSelection[selectedDataType.value] || {}),
					[section]: data
				}
			};
		});
		setCurrentFieldSelection(fieldSelection);
	}, [ currentFieldSelection, selectedDataType ]);

	const duplicatePresetToFieldSelection = useCallback((_preset=null) => {
		const preset = _preset || selectedPreset;
		if(!selectedDataType) { return }
		if(!preset) { return }
		if(preset.config.dataType !== selectedDataType.value) { return }
		setCurrentFieldSelection({
			...currentFieldSelection,
			[selectedDataType.value]: preset.config.fields
		});
	}, [currentFieldSelection, selectedDataType, selectedPreset]);

	const resetCurrentFieldSelection = useCallback(_dataTypeId => {
		if(!selectedDataType) { return }
		const { [_dataTypeId ?? selectedDataType.value]: dataTypeId, ...newState } = currentFieldSelection;
		setCurrentFieldSelection(newState);
	}, [ selectedDataType, currentFieldSelection ]);
	// END PRESET

	const [isTimeRange, setIsTimeRange] = useState(false); // for P10
	const [isDateRange, setIsDateRange] = useState(false); // for daily
	const [isMonthRange, setIsMonthRange] = useState(false); // for monthly
	const [isYearRange, setIsYearRange] = useState(false); // for monthly

	// set default time/date/month range
	const timeRange = useMemo( () => ({
		startDate: moment().subtract(7, "days").startOf('day'), // timeDisplayed
		endDate: moment().subtract(1, "days").startOf('day'),
	}), []);
	const dateRange = useMemo(() => ({
		startDate: moment().subtract(7, "days").startOf('day'), // dayDisplayed
		endDate: moment().subtract(1, "days").startOf('day'),
	}), []);
	const monthRange = useMemo(() => ({
		startDate: moment().subtract(1, "months").startOf('month'),
		endDate: moment().startOf('month'),
	}), []);
	const yearRange = useMemo(() => ({
		startDate: moment().subtract(1, "year").startOf('year'),
		endDate: moment().startOf('year'),
	}), []);
	
	// input predefined ranges
	const dateRangePickerRanges = useMemo(() => ({
		Today: [moment().startOf('day'), moment()],
		Yesterday: [
			moment().subtract(1, "days").startOf('day'),
			moment().subtract(1, "days").endOf('day'),
		],
		"Last 7 Days": [moment().subtract(6, "days"), moment()],
		"Last 30 Days": [moment().subtract(29, "days"), moment()],
		"This Month": [
			moment().startOf("month"),
			moment(),
		],
		"Last Month": [
			moment().subtract(1, "month").startOf("month"),
			moment().subtract(1, "month").endOf("month"),
		],
	}), []);

	const momentRangeRef = useRef({}); // this will fix usecallback referencing old value of dependencies
	const momentRange = useMemo(() => {
		const defaultDateRange = isTimeRange ? timeRange : isDateRange ? dateRange : isMonthRange ? monthRange : isYearRange ? yearRange : dateRange;
		let momentRange = currentFieldSelection?.[selectedDataType?.id]?.DATE_RANGE?.range || selectedPreset?.config?.fields?.DATE_RANGE?.range || defaultDateRange;
		momentRangeRef.current = momentRange;
		// return evaluated moment range
		return momentRange;
	}, [isTimeRange, timeRange, isDateRange, dateRange, isMonthRange, monthRange, isYearRange, yearRange, currentFieldSelection, selectedDataType, selectedPreset]);

	// fix <DateRangePicker /> bug
	const [ dateRangePickerKey, setDateRangePickerKey ] = useState(new Date().getTime()); // fix <DateRangePicker /> bug: it watches props change only once
	useEffect(() => {
		setDateRangePickerKey(new Date().getTime()); // this will unmount and remount <DateRangePicker />, just to fix the library bug
	}, [ momentRange, selectedPreset, currentFieldSelection, isTimeRange, isDateRange ]);

	const handleDateTimeMonthRangePickerChange = useCallback((...args) => {
		let dateRange = null;
		if(isTimeRange || isDateRange) {
			const [ e, picker ] = args;
			let startDate = picker.startDate;
			let endDate = picker.endDate;
			if(picker.chosenLabel === 'Custom Range') {
				if(!isTimeRange) {
					startDate = startDate.startOf('day');
					endDate = endDate.startOf('day');
				}
				dateRange = { range: { startDate, endDate } } // moment dates
			}
			if(picker.chosenLabel !== 'Custom Range') {
				dateRange = { label: picker.chosenLabel, range: { startDate, endDate } }; // label => a string like : 'Last Month'
			}
		}
		if(isMonthRange || isYearRange) {
			let [ startDate, endDate ] = args[0]; // these are JS dates
			if (!startDate && !endDate) { return }
			startDate = startDate && moment(pickerTimeTolocalTime(startDate));
			endDate = endDate && moment(pickerTimeTolocalTime(endDate));
			dateRange = { range: { startDate, endDate } }
		}
		// copy preset fields state to currenFieldSelection
		const newStateUpdates = [{ section: 'DATE_RANGE', data: dateRange }];
		if(selectedPreset && selectedPreset.config.dataType === selectedDataType?.id && (!currentFieldSelection?.[selectedDataType?.id]?.STANDARD && !currentFieldSelection?.[selectedDataType?.id]?.ADVANCED)) {
			if(selectedPreset.config.fields?.STANDARD) {
				newStateUpdates.push({ section: 'STANDARD', data: selectedPreset.config.fields.STANDARD });
			}
			if(selectedPreset.config.fields?.ADVANCED) {
				newStateUpdates.push({ section: 'ADVANCED', data: selectedPreset.config.fields.ADVANCED });
			}
		}
		// update currentFieldSelection
		handleCurrentFieldSelectionChange(newStateUpdates);
		// clear preset
		if(selectedPreset && !isEditPreset && !isCreatePreset) {
			clearPreset();
		}
		// refresh table
		if(!isEditPreset && !isCreatePreset) {
			setIsReBuildTable(true);
		}
	}, [isTimeRange, isDateRange, isMonthRange, isYearRange, selectedPreset, selectedDataType, currentFieldSelection, handleCurrentFieldSelectionChange, isEditPreset, isCreatePreset, clearPreset]);

	const originalDataLabelNames = useRef([]);

	const [colStandardContent, setColStandardContent] = useState([]);
	const [colStandardTitle, setColStandardTitle] = useState("");
	const [isStandardCollapsed, setStandardCollapsed] = useState(true);

	const [colAdvancedContent, setColAdvancedContent] = useState([]);
	const [isAdvancedCollapsed, setAdvancedCollapsed] = useState(true);

	const [onOffLabels, setOnOffLabels] = useState([]);
	const [labelON, setLabelON] = useState("");
	const [labelOFF, setLabelOFF] = useState("");

	const [ bessLabels, setBessLabels ] = useState({});

	const [loader, setLoader] = useState(false);
	const [showLoader, setShowLoader] = useState({ position: "relative" });

	const [isReset, setIsReset] = useState(false);

	const user = useUser();
	const kcID = user.sub;
	
	const isITStaff = user.roles.includes('_GY:IT');

	const rowClass = "MuiTableRow-root";
	const cellClass =
		"MuiTableCell-root MuiTableCell-body MuiTableCell-alignLeft";
	const totalStyle = useMemo(() => ({
		fontWeight: "bold",
		fontSize: "1.3rem",
	}), []);

	function loading(isNeeded) {
		if (isNeeded) {
			setLoader(true);
			setShowLoader({ position: "fixed" });
		} else {
			setLoader(false);
			setShowLoader({ position: "relative" });
		}
	}

	function turnOff(what) {
		switch (what) {
			case "fromSite":
				setIsSite(false);
				setSelectedSites([]);
				setSiteSelectFilterBySiteState(null);
				setIsDataType(false);
				setIsStandard(false);
				setIsAdvanced(false);
				setIsDateRange(false);
				setIsTimeRange(false);
				setIsMonthRange(false);
				setIsYearRange(false);
				setIsTable(false);
				setTableLoading(false);
				setSelectedDataType(null);
				selectedDataTypeRef.current = null;
				setIsCreatePreset(false);
				setEditingPreset(null);
				break;

			case "fromDataType":
				setIsDataType(false);
				setIsStandard(false);
				setIsAdvanced(false);
				setIsDateRange(false);
				setIsTimeRange(false);
				setIsMonthRange(false);
				setIsYearRange(false);
				setIsTable(false);
				setTableLoading(false);
				setSelectedDataType(null);
				selectedDataTypeRef.current = null;
				break;

			case "fromStandard":
				setIsStandard(false);
				setIsAdvanced(false);
				setIsDateRange(false);
				setIsTimeRange(false);
				setIsMonthRange(false);
				setIsYearRange(false);
				setIsTable(false);
				setTableLoading(false);
				setIsReset(true);
				setColStandardContent([]);
				setColAdvancedContent([]);
				setColStandardTitle("");
				break;
			
			case "fromTable":
				setTableFoot([]);
				setTableData([]);
				setIsTable(false);
				setTableLoading(false);
				break;

			default:
				setIsContract(false);
				setIsSite(false);
				setSelectedSelectBy(null);
				setSelectedContracts(null);
				selectedContractsRef.current = null;
				setSelectedSites([]);
				setSiteSelectFilterBySiteState(null);
				setIsDataType(false);
				setIsStandard(false);
				setIsAdvanced(false);
				setIsDateRange(false);
				setIsTimeRange(false);
				setIsMonthRange(false);
				setIsYearRange(false);
				setIsTable(false);
				setTableLoading(false);
				setSelectedDataType(null);
				selectedDataTypeRef.current = null;
				setIsCreatePreset(false);
				setEditingPreset(null);
				break;
		}
	}

	const getListContract = useCallback(() => {
		const uri =
			selectedCountry === "ALL"
				? "/select/contracts"
				: `/select/contracts/${selectedCountry}`;

		GYAxios.get(uri).then((res) => {
			if (res.data.error !== undefined) {
				Noti.create({
					type: "e",
					msg: "Something is wrong. Please contact IT department!",
				});
				return;
			} else {
				if (Array.isArray(res.data) && res.data.length > 0) {
					setListContract(res.data);
					listContractRef.current = res.data;
				} else {
					Noti.create({
						type: "e",
						msg: "Something is wrong. Please contact IT department!",
					});
					return;
				}
			}
		});
	}, [selectedCountry]);

	const onChangeSelectedBy = useCallback(selectedSelectBy => {
		turnOff();
		setSelectedSelectBy(selectedSelectBy);
		if(!selectedSelectBy?.value) { return }
		if (selectedSelectBy.value === "client") {
			setIsSite(true);
		} else if (selectedSelectBy.value === "contract") {
			setIsContract(true);
			getListContract();
		}
	}, [getListContract]);

	function onChangeSelectedByWithClearPreset(selectedSelectBy) {
		onChangeSelectedBy(selectedSelectBy);
		clearPreset();
	}

	const getListSite = useCallback(async (list, filter) => {
		if (!list?.length) return;

		const keyword = list.map((v) => v.value);
		const thisFilter = filter;
		const uri =
			selectedCountry === "ALL"
				? `/select/multisites/${thisFilter}`
				: `/select/multisites/${thisFilter}/${selectedCountry}`;
		return GYAxios.post(uri, { keyword: keyword }).then((res) => {
			if (res.data.error || !res.data.length) {
				Noti.create({
					type: "e",
					msg: "Something is wrong. Please contact IT department!",
				});
				return;
			}
			const resSites = res.data;
			const resSiteIds = resSites.map(resSite => resSite.site_id);
			const allSites = clientOptions.map(clientOption => clientOption.options.map(siteOption => siteOption.value)).flat(1);
			// for GY user, show all sites
			if(userAttr.type === 1) {
				setSiteSelectFilterBySiteState(allSites.filter(site => resSiteIds.includes(site.site_id)));
			}
			// for client with all sites, show all sites
			if(userAttr.type !== 1 && userAttr.site_id === 'all') {
				setSiteSelectFilterBySiteState(allSites.filter(site => resSiteIds.includes(site.site_id)));
			}
			// for client with limited sites, show limited sites
			if(userAttr.type !== 1 && userAttr.site_id !== 'all') {
				const userSiteIds = userAttr.site_id.split(',').map(siteId => parseInt(siteId));
				const filteredSite = resSites.filter((v) => userSiteIds.includes(v.value));
				setSiteSelectFilterBySiteState(filteredSite);
			}

			const clientSiteSelected = resSites.map((v) => v.client_id);
			const uniqueueClientId = [...new Set(clientSiteSelected)];
			const objectClientSite = uniqueueClientId.map((clientId) => ({ id: clientId }));
			setSelectedClient(objectClientSite);
		});
	}, [clientOptions, selectedCountry, userAttr]);

	const onChangeClientContract = useCallback(async selectedContracts => {
		setSelectedContracts(selectedContracts);
		if(!selectedContracts?.length) { return turnOff("fromSite") }
		setIsSite(true);
		setSelectedDataType(null);
		selectedDataTypeRef.current = null;
		await getListSite(selectedContracts, 'contract');
	}, [getListSite]);

	function onChangeClientContractWithClearPreset(selectedContracts) {
		onChangeClientContract(selectedContracts);
		clearPreset();
	}

	function onChangeCountry(obj) {
		setSelectedCountry(obj.value);
	}

	const filterOutHiddenDataLabelNames = useCallback((variables={ user, selectedSites, selectedCountry }) => {
		return originalDataLabelNames.current.filter(data_label_name => {
			// hide by roles
			if(data_label_name.rolesHidden) {
				try {
					const rolesHidden = JSON.parse(data_label_name.rolesHidden);
					for(let setOfRoles of rolesHidden) {
						if(setOfRoles.every(role => user.roles.includes(role))) {
							return false;
						}
					}
				} catch(_) { /* pass */ }
			}
			// hide by condition
			if(data_label_name.hiddenFn?.trim()) {
				const condition = dataLabelNameHiddenFns[data_label_name.hiddenFn.trim()]?.(variables);
				if(condition) {
					return false;
				}
			}
			return true;
		});
	}, [ user, selectedSites, selectedCountry ]);

	const replaceOnOffLabel = useCallback((dataLabelNames, label={labelON, labelOFF}) => {
		return dataLabelNames.map((dataLabelName) => {
			if (dataLabelName.label.includes("ONLABEL")) {
				return { ...dataLabelName, label: dataLabelName.label.replace("ONLABEL", label.labelON) };
			} else if (dataLabelName.label.includes("OFFLABEL")) {
				return { ...dataLabelName, label: dataLabelName.label.replace("OFFLABEL", label.labelOFF) };
			} else {
				return dataLabelName;
			}
		});
	}, [labelON, labelOFF]);

	const replaceBessOnOffStdLabel = useCallback(dataLabelNames => dataLabelNames.map(dataLabelName => {
		const country = selectedSitesCountry === 'ALL' ? 'TH' : selectedSitesCountry;
		if(bessLabels?.[country]) {
			const peakKeywords = ["BESS_ONLABEL", "BESS_OFFLABEL", "BESS_STDLABEL"];
			for(let peakKeyword of peakKeywords) {
				if(dataLabelName.label.includes(peakKeyword)) {
					return { ...dataLabelName, label: dataLabelName.label.replace(peakKeyword, bessLabels[country][peakKeyword]) };
				}
			}
		}
		return dataLabelName;
	}), [selectedSitesCountry, bessLabels]);

	// filter out data label names that is that is not child
	const filterOutChildDataLabelNames = useCallback((dataLabelNames) => {
		return dataLabelNames.filter(label => typeof(label.parentId) !== 'number');
	}, []);

	const getDataLabelNamesWithChildren = useCallback((dataLabelNames) => {
		const newDataLabelNames = [];
		dataLabelNames.forEach((dataLabelName) => {
			const children = originalDataLabelNames.current.filter(label => label.parentId === dataLabelName.id);
			if(children.length) {
				children.sort((a, b) => a.ordering.localeCompare(b.ordering));
				newDataLabelNames.push(...children);
			}
			if(!children.length && (dataLabelName.value && dataLabelName.tableBelong !== '_ROOT')) {
				newDataLabelNames.push(dataLabelName);
			}
		});
		return newDataLabelNames;
	}, []);

	// hide standard checkboxes and advanced selects based on
	// roles and conditions
	const filterColStandardContent = useCallback((options={ isP10: false }, variables={ user, selectedSites: selectedSitesRef.current, selectedCountry }) => {
		// evaluate condition
		const dataLabelNames = filterOutHiddenDataLabelNames(variables);
		if(options.isP10) { return dataLabelNames }
		let standardLabelNames = dataLabelNames.filter(({ standard }) => standard === 1);
		standardLabelNames = replaceBessOnOffStdLabel(standardLabelNames);
		standardLabelNames = replaceOnOffLabel(standardLabelNames);
		standardLabelNames = filterOutChildDataLabelNames(standardLabelNames);
		setColStandardContent(standardLabelNames);
	}, [user, selectedCountry, filterOutHiddenDataLabelNames, replaceBessOnOffStdLabel, replaceOnOffLabel, filterOutChildDataLabelNames]);

	const getP10Req = useCallback(async () => {
		if (!selectedSitesRef.current.length) return;

		setIsDateRange(true);
		loading(true);

		const table_name = selectedSitesRef.current[0].table_name;
		const EDMI_meter = selectedSitesRef.current[0].EDMI_meter;
		const nb_inj_points = selectedSitesRef.current[0].nb_inj_points;
		const isCL = userAttr.type === 1 ? 0 : 1;
		const isNoPV = selectedSitesRef.current[0].is_no_PV;
		const uri = `/data_download/p10/columns/${isCL}/${table_name}`;
		const title = table_name;

		let colInIndividualSite = [];
		return GYAxios.post("/general_info/get_columns", { table_name }).then(
			(res_cols) => {
				if (res_cols.data.error !== undefined) {
					Noti.create({
						type: "e",
						msg: `${res_cols.data.error}`,
					});
					return;
				}

				colInIndividualSite = res_cols.data.map((v) => v.Field);

				const split = [
					"rate1_production",
					"rate1_today",
					"rate2_production",
					"rate2_today",
				];

				const nonsplit = [
					"grand_total_production",
					"grand_total_production_today",
				];

				let filteredColSite = [];
				if (EDMI_meter === 1) {
					filteredColSite = colInIndividualSite.filter(
						(v) => !nonsplit.includes(word(v, " ", 1))
					);
				} else if (EDMI_meter === 0) {
					filteredColSite = colInIndividualSite.filter(
						(v) => !split.includes(word(v, " ", 1))
					);
				}

				return GYAxios.get(uri).then((res) => {
					if (res.data.error === undefined) {
						originalDataLabelNames.current = res.data;
						res.data = filterColStandardContent({ isP10: true });
						res.data = filterOutChildDataLabelNames(res.data);
						const standard = res.data.filter(({ standard }) => standard === 1);
						const matchedStandard = standard.filter((v) =>
							filteredColSite.includes(v.value)
						);
						const groups = res.data.filter(({ standard }) => standard === 0);
						const keywords = groups.map((v) => v.value);

						const advanced = groups.map((v, i) => {
							const eachGroup = filteredColSite.filter((vv) =>
								vv.includes(keywords[i])
							);

							return { ...v, children: eachGroup };
						});
						const matchedAdvanced = advanced.filter(
							({ children }) => children.length > 0
						);

						const detailedAdvanced = matchedAdvanced.map((v) => {
							switch (v.value) {
								case "irradiance":
									if (v.children.length === 1) {
										v.children = [
											{
												value: v.children[0],
												label: `${v.label}`,
												group: `${v.value}`,
											},
										];
									} else {
										const i = v.children.map((vv) => {
											if (word(vv, "_", 1) === "irradiance") {
												vv = {
													value: vv,
													label: `Average ${v.label}`,
												};
											} else {
												vv = {
													value: vv,
													label: `${cfl(word(v.label, "(", 2))} ${word(
														vv,
														"_",
														1
													)} (${cfl(word(v.label, "(", 1))}`,
												};
											}

											vv.group = `${v.value}`;
											return vv;
										});

										v.children = i;
									}

									v.order = "a";
									break;

								case "grand_total_production":
									let gra = v.children.map((vv) => {
										if (vv === "grand_total_production") {
											vv = {
												value: vv,
												label: `${v.label} Index (kWh)`,
											};
										} else {
											vv = {
												value: vv,
												label: `${v.label} ${cfl(word(vv, "_", 1))} (kWh)`,
											};
										}

										vv.group = `${v.value}`;
										return vv;
									});

									// remove injection point index first
									gra = gra.filter(vv => !/^inj_/.test(vv.value));

									// then add injection point index if more than 1 injection points
									if(nb_inj_points >= 2) {
										for(let i=0; i<nb_inj_points; i++) {
											const fI = ("0" + (i+1)).slice(-2);
											gra.push({
												label: `${v.label} Meter ${fI} (kWh)`,
												value: `inj_${fI}_grand_production`,
												group: `${v.value}`,
											});
										}
									}

									v.children = gra;
									v.order = "d";
									break;

								case "rate":
									let ra = v.children.map((vv) => {
										switch (word(vv, "e", 1)) {
											case "1_production":
												vv = {
													value: vv,
													label: `${labelOFF} Production (kWh)`,
												};
												break;

											case "2_production":
												vv = {
													value: vv,
													label: `${labelON} Production (kWh)`,
												};
												break;
											
											case "3_production":
												vv = {
													value: vv,
													label: `Off hours Production (kWh)`,
												};
												break;

											case "1_today":
												vv = {
													value: vv,
													label: `${labelOFF} Production Today (kWh)`,
												};
												break;

											case "2_today":
												vv = {
													value: vv,
													label: `${labelON} Production Today (kWh)`,
												};
												break;

											case "3_today":
												vv = {
													value: vv,
													label: `Off hours Production Today (kWh)`,
												};
												break;

											default:
												break;
										}

										vv.group = `${v.value}`;
										return vv;
									});

									// remove injection point rate first
									ra = ra.filter(vv => !/^inj_/.test(vv.value));

									// then add injection point rate if more than 1 injection points
									if(nb_inj_points >= 2) {
										for(let i=0; i<nb_inj_points; i++) {
											const fI = ("0" + (i+1)).slice(-2);
											ra.push({
												label: `${labelOFF} Meter ${fI} Production (kWh)`,
												value: `inj_${fI}_rate1_production`,
												group: `${v.value}`,
											});
											ra.push({
												label: `${labelON} Meter ${fI} Production (kWh)`,
												value: `inj_${fI}_rate2_production`,
												group: `${v.value}`,
											});
										}
									}

									v.children = ra;
									v.order = "e";
									break;

								case "INV_":
									const totalInv = Math.floor(v.children.length / 6);
									const newArray = [];
									for (let i = 1; i < totalInv + 1; i++) {
										const fI = ("0" + i).slice(-2);
										newArray.push({
											label: `${v.label.split("_")[0]} ${fI}`,
											value: `${v.value}${fI}`,
											group: `${v.value}`,
										});
									}
									const tempGrand = v.children;

									v.grandChildren = tempGrand;
									v.children = newArray;
									v.order = "b";
									break;

								case "grid_phase":
									const g = v.children.map((vv) => {
										vv = {
											value: vv,
											label: `${v.label} ${word(vv, "e", 1)} (kW)`,
											group: `${v.value}`,
										};

										return vv;
									});

									v.children = g;
									v.order = "s";
									break;

								case "voltage_phase":
									const vol = v.children.map((vv) => {
										vv = {
											value: vv,
											label: `${v.label} ${word(vv, "e", 1)} (V)`,
											group: `${v.value}`,
										};

										return vv;
									});

									v.children = vol;
									v.order = "t";
									break;

								case "amp_phase":
									const am = v.children.map((vv) => {
										vv = {
											value: vv,
											label: `${v.label} ${word(vv, "e", 1)} (A)`,
											group: `${v.value}`,
										};

										return vv;
									});

									v.children = am;
									v.order = "u";
									break;

								case "wind_dir":
									const di = v.children.map((vv) => {
										vv = {
											value: vv,
											label: `${v.label}`,
											group: `${v.value}`,
										};

										return vv;
									});

									v.children = di;
									v.order = "v";
									break;

								case "water_level":
									const wa = v.children.map((vv) => {
										vv = {
											value: vv,
											label: `${v.label}`,
											group: `${v.value}`,
										};

										return vv;
									});

									v.children = wa;
									v.order = "w";
									break;

								case "DGPT2_":
									const dg = v.children.map((vv) => {
										const val = vv;
										vv = {
											group: `${v.value}`,
											order: "x",
										};

										switch (word(val, "_", 2)) {
											case "istrip":
												switch (word(val, "_", 1)) {
													case "temp":
														vv.label = "DGPT2 Trip Temperature";
														vv.value = val;
														break;

													case "oilpressure":
														vv.label = "DGPT2 Trip Oil Pressure";
														vv.value = val;
														break;

													case "oillevel":
														vv.label = "DGPT2 Trip Oil Level";
														vv.value = val;
														break;

													default:
														break;
												}

												break;

											case "isalarm":
												vv.label = "DGPT2 Alarm Temperature";
												vv.value = val;
												break;

											default:
												break;
										}

										return vv;
									});

									v.children = dg;
									break;

								case "EDMI":
									const ed = v.children.map((vv) => {
										if (word(vv, "_", 1) === "EDMI") {
											vv = {
												value: vv,
												label: `Average ${v.label}`,
											};
										} else {
											vv = {
												value: vv,
												label: `${v.label} ${word(vv, "_", 1)}`,
											};
										}

										vv.group = `${v.value}`;
										return vv;
									});

									v.children = ed;
									v.order = "y";
									break;

								case "wind_speed":
									if (v.children.length === 1) {
										v.children = [
											{
												value: v.children[0],
												label: `${v.label}`,
												group: `${v.value}`,
											},
										];
									} else {
										const sp = v.children.map((vv) => {
											if (word(vv, "_", 1) === "speed") {
												vv = {
													value: vv,
													label: `Average ${v.label}`,
												};
											} else {
												vv = {
													value: vv,
													label: `${v.label} ${word(vv, "_", 1)}`,
												};
											}

											vv.group = `${v.value}`;
											return vv;
										});

										v.children = sp;
									}
									v.order = "z";
									break;
								
								case "BESS_":
									// add BESS parameters that will be shared by all BESS cabinets
									const bessVals = v.children;
									const bessOptNames = bessVals
										.map(field => field.replaceAll("_", " ").trim())
										.map(field => {
											const country = selectedSitesCountry === 'ALL' ? 'TH' : selectedSitesCountry;
											field = field.replace(/ ON /ig, ` ${bessLabels[country]['BESS_ONLABEL']} `);
											field = field.replace(/ OFF /ig, ` ${bessLabels[country]['BESS_OFFLABEL']} `);
											field = field.replace(/ STD /ig, ` ${bessLabels[country]['BESS_STDLABEL']} `);
											return field;
										});
									v.children = bessVals.map((val, idx) => ({
										value: val,
										label: bessOptNames[idx],
										group: v.value,
									}));
									v.order = "bb";
									break;

								default:
									break;
							}

							return v;
						});

						if(!isNoPV) {

							detailedAdvanced.push({
								children: [
									{
										group: "InvData",
										label: "Inverter Total (kWh)",
										value: "_Total",
									},
									{
										group: "InvData",
										label: "Inverter Today (kWh)",
										value: "_Today",
									},
									{
										group: "InvData",
										label: "Inverter Power (kW)",
										value: "_Power",
									},
									{
										group: "InvData",
										label: "Inverter State",
										value: "_State",
									},
									{
										group: "InvData",
										label: "Inverter Alarm State",
										value: "_Alarm_State",
									},
									{
										group: "InvData",
										label: "Inverter Control (%)",
										value: "_Control",
									},
								],
								label: "Inverter Data",
								order: "c",
								readOnly: 0,
								standard: 0,
								value: "inverter_data",
							});
						}


						setColStandardContent(matchedStandard);
						setColAdvancedContent(detailedAdvanced);
						setColStandardTitle(title);
					} else {
						Noti.create({
							type: "e",
							msg: "There is something wrong",
						});
						setIsStandard(false);
						setIsAdvanced(false);
					}

					loading(false);
				});
			}
		);
	}, [filterColStandardContent, filterOutChildDataLabelNames, labelOFF, labelON, userAttr.type, bessLabels, selectedSitesCountry]);

	const getDataLabelNames = useCallback(async (route) => {
		loading(true);

		const isCL = userAttr.type === 1 ? 0 : 1;
		const uri = `/data_download/${route}/columns/${isCL}`;
		const title = route === "daily_calc" ? "Daily Data" : route === "monthly_calc" ? "Monthly Data" : route === "yearly_calc" ? "Yearly Data" : "";

		switch (route) {
			case "daily_calc":
				setIsDateRange(true);
				break;

			case "monthly_calc":
				setIsMonthRange(true);
				break;

			case "yearly_calc":
				setIsYearRange(true);
				break;

			case "inverter_data":
				break;

			default:
				break;
		}

		return GYAxios.get(uri).then((res) => {
			if (res.data.error === undefined) {
				originalDataLabelNames.current = res.data;
				filterColStandardContent();
				setColStandardTitle(title);
			} else {
				Noti.create({
					type: "e",
					msg: "There is something wrong",
				});
				setIsStandard(false);
			}

			loading(false);
		});
	}, [filterColStandardContent, userAttr.type]);

	const getProductionUri = useCallback((route) => {
		if (!Array.isArray(selectedSitesRef.current)) { return }
		if (!selectedSitesRef.current?.length) { return }
		if(!momentRangeRef.current.startDate || !momentRangeRef.current.endDate) { return }

		const site_id = selectedSitesRef.current[0].site_id;
		let fromDate =  formatDate(momentRangeRef.current.startDate);
		let toDate = formatDate(momentRangeRef.current.endDate);

		const uri = `/${route}/site/${site_id}/period/${fromDate}/${toDate}/order/dateData/DESC`;
		return uri;
	}, []);

	const getProduction = useCallback(async (route) => {
		const uri = getProductionUri(route);
		if(!uri) { return }

		if(route === "monthly" && monthRange.length === 2 && !monthRange[1]) {
			return;
		}

		setIsTable(true);
		setTableLoading(true);
		
		let ON = 0;
		let OFF = 0;
		let TOTAL = 0;

		return GYAxios.get(uri).then((res) => {
			if (
				Array.isArray(res.data) &&
				res.data.length > 0 &&
				res.data.error === undefined
			) {
				const data = res.data.map((row) => {
					const deliveredON =
						route === "monthly" ? row.solar_ON : row.E_delivered_ON;
					const deliveredOFF =
						route === "monthly" ? row.solar_OFF : row.E_delivered_OFF;
					const deliveredTotal =
						route === "monthly" ? row.solar_total : row.E_delivered_total;

					// Sum up total for table footer
					ON += deliveredON;
					OFF += deliveredOFF;
					TOTAL += deliveredTotal;

					return {
						dateData: route === 'monthly' ? month(row.dateData) : date(row.dateData),
						deliveredON,
						deliveredOFF,
						deliveredTotal,
					};
				});

				const tableSumData = {
					ON: ON.toFixed(3),
					OFF: OFF.toFixed(3),
					TOTAL: TOTAL.toFixed(3),
				};

				let title = "";
				route === "monthly"
					? (title = "Monthly Estimated Production")
					: (title = "Daily Delivered Production");

				let fileName = "";
				route === "monthly"
					? (fileName = "Monthly Estimated Production")
					: (fileName = "Daily Delivered Production");

				setTableFoot(
					<tfoot>
						<tr className={rowClass}>
							<td className={cellClass} style={totalStyle}>
								TOTAL
							</td>
							<td className={cellClass} style={totalStyle}>
								{tableSumData.ON}
							</td>
							<td className={cellClass} style={totalStyle}>
								{tableSumData.OFF}
							</td>
							<td className={cellClass} style={totalStyle}>
								{tableSumData.TOTAL}
							</td>
						</tr>
					</tfoot>
				);

				setTableFilename(`${fileName}_${selectedSitesRef.current[0].table_name}`);
				setTableTitle(title);
				setTableCol(prodcols);
				setTableData(data);
				setTableLoading(false);
			} else {
				Noti.create({
					type: "e",
					msg: "Something is wrong. Please contact IT department!",
				});
				return;
			}
		});
	}, [getProductionUri, monthRange, prodcols, totalStyle]);

	const getSiteInfo = useCallback(async () => {
		if (!selectedSitesRef.current?.length) return;
		loading(true);
		const uri = "/data_download/sites/columns/0";
		const title = "Site Information";

		return GYAxios.get(uri).then((res) => {
			if (res.data.error === undefined) {
				originalDataLabelNames.current = res.data;
				filterColStandardContent();
				setColStandardTitle(title);
			} else {
				Noti.create({
					type: "e",
					msg: "There is something wrong",
				});
				setIsStandard(false);
			}

			loading(false);
		});
	}, [filterColStandardContent]);

	const [ formatTableDataWorker ] = useWorker(formatTableData, {
		remoteDependencies: ['https://cdnjs.cloudflare.com/ajax/libs/dayjs/1.11.11/dayjs.min.js']
	});

	const onChangeType = useCallback(async (dataType) => {
		if (dataType !== null) {
			if (dataType.value !== null) {
				const id = dataType.id;
				setSelectedDataType(dataType);
				selectedDataTypeRef.current = dataType;
				turnOff("fromStandard");

				switch (id) { // set state then delay for state to be updated, then fetch data from APIs
					case 1:
						setIsTimeRange(true);
						setIsStandard(true);
						setIsAdvanced(true);
						await delay();
						await getP10Req();
						break;
					case 2:
						setIsStandard(true);
						setIsDateRange(true);
						await delay();
						await getDataLabelNames("daily_calc");
						break;
					case 3:
						setIsStandard(true);
						setIsMonthRange(true);
						await delay();
						await getDataLabelNames("monthly_calc");
						break;
					case 4:
						setIsDateRange(true);
						await delay();
						await getProduction("daily");
						break;
					case 5:
						setIsMonthRange(true);
						await delay();
						await getProduction("monthly");
						break;
					case 6:
						setIsStandard(true);
						await delay();
						await getSiteInfo();
						break;
					case 7:
						setIsStandard(true);
						await delay();
						await getDataLabelNames("yearly_calc");
						break;
					case 8:
						setIsStandard(true);
						await getDataLabelNames("inverter_info");
						break;
					default:
						break;
				}
			} else {
				turnOff("fromStandard");
			}
		} else {
			turnOff("fromStandard");
		}
	}, [getDataLabelNames, getP10Req, getProduction, getSiteInfo]);

	const onChangeTypeWithDispatch = useCallback(dataType => {
		onChangeType(dataType);
		PresetEventEmitter.dispatch(presetEventNames.SET_SELECT_PRESET, { from: 'LAST_USED', dataType: dataType.id });
	}, [ onChangeType ]);

	const updateDataType = useCallback(async (dataTypes, reloadP10Form=true) => {
		if(!selectedSitesRef.current?.length || !dataTypes?.length) { return }
		// re-build form when there is only 1 data type
		if(dataTypes.length === 1) {
			await onChangeType(dataTypes[0]);
		}
		// rebuild form if possible
		if(dataTypes.length > 1 && selectedDataType && dataTypes.find(dataType => dataType.id === selectedDataType.id)) {
			if(selectedDataType.id === 1 && reloadP10Form) { // currently select P10 data type, then reload p10 form
				await onChangeType(dataTypes.find(dataType => dataType.id === 1));
			}
		}
		// selected data type cannot fit in available data types
		if(dataTypes.length > 1 && selectedDataType && !dataTypes.find(dataType => dataType.id === selectedDataType.id)) {
			setSelectedDataType(null);
			selectedDataTypeRef.current = null;
			turnOff('fromStandard');
			return;
		}
	}, [onChangeType, selectedDataType]);

	const getListDataType = useCallback(async (sites) => {
		if (!sites?.length) { return }
		dataTypeReqSource = axios.CancelToken.source();
		return GYAxios.post(`/select/dataType/${isGY}`, sites, { cancelToken: dataTypeReqSource.token }).then((res) => {
			const dataTypes = res.data;
			if (dataTypes.error) {
				Noti.create({ type: "e", msg: "Something is wrong. Please contact IT department!" });
				return;
			}
			if(!dataTypes.length) {
				Noti.create({ type: "e", msg: "Something is wrong. Please contact IT department!" });
				return;
			}
			return dataTypes;
		});
	}, [isGY]);

	const handleSitesSelectChange = useCallback(async (selectedSites, { reloadP10Form, clearPreset: shouldClearPreset }={}) => {
		setIsListTypeLoading(true);
		const selectedSitesPayload = selectedSites.map(site => {
			const { site_id, contract_id, client_id, operation_status } = site;
			return { site_id, contract_id, client_id, operation_status };
		});
		const selectedClientIds = [...new Set(selectedSites.map(site => site.client_id))].map(clientId => ({ id: clientId }));
		setSelectedClient(selectedClientIds);
		setSelectedSites(selectedSites);
		setIsDataType(true);
		selectedSitesRef.current = selectedSites;

		// clear preset if possible
		if(shouldClearPreset && selectedPreset && !isEditPreset && !isCreatePreset) {
			duplicatePresetToFieldSelection();
			clearPreset();
		}

		// get list of data type
		if(dataTypeReqSource) { dataTypeReqSource.cancel() } // cancel pending dataType req
		let dataTypes;
		try {
			dataTypes = await getListDataType(selectedSitesPayload);
		} catch(err) {
			if(axios.isCancel(err)) { return } // if dataType req is canceled, do nothing
			console.error(err);
		}
		dataTypeReqSource = null;
		// update list of data type
		if(dataTypes) {
			await updateDataType(dataTypes, reloadP10Form);
			setListType(dataTypes);
			listTypeRef.current = dataTypes;
			setIsListTypeLoading(false);
		}
	}, [selectedPreset, isEditPreset, isCreatePreset, getListDataType, duplicatePresetToFieldSelection, clearPreset, updateDataType]);

	const getStandardCols = useCallback(() => {
		const dirtyStandardValues = currentFieldSelection?.[selectedDataType.id]?.STANDARD;
		const presetStandardValues = selectedPreset?.config?.fields?.STANDARD;
		const defaultStandardValues = colStandardContent.filter(col => col.standard && col.readOnly).map(col => col.value);
		let checkedStandardValues = dirtyStandardValues || presetStandardValues || defaultStandardValues;
		let checkedStandardCols = colStandardContent.filter(col => checkedStandardValues.includes(col.value));
		checkedStandardCols = checkedStandardCols && getDataLabelNamesWithChildren(checkedStandardCols);
		return checkedStandardCols;
	}, [ currentFieldSelection, selectedPreset, colStandardContent, selectedDataType, getDataLabelNamesWithChildren ]);

	const getAdvancedCols = useCallback(() => {
		if(!colAdvancedContent?.length) { return [] }

		// available values
		const availableValues = colAdvancedContent.map(col => col.children.map(option => option.value)).flat(1);

		// advanced selected keys
		const dirtyAdvancedValues = currentFieldSelection?.[selectedDataType.id]?.ADVANCED;
		const presetAdvancedValues = selectedPreset?.config?.fields?.ADVANCED;
		const advancedValues = {...(dirtyAdvancedValues || presetAdvancedValues || {})};

		// handle select all
		for(let key of Object.keys(advancedValues)) {
			if(!advancedValues.hasOwnProperty(key)) { continue }
			if(advancedValues[key].find(option => option.value === '_ALL')) {
				const otherOptions = colAdvancedContent.find(select => select.children?.[0]?.group === key)?.children || [];
				advancedValues[key] = [...otherOptions];
			}
		}

		let selectedKeys = advancedValues ? Object.values(advancedValues).flat(1) : [];

		selectedKeys = selectedKeys.filter(option => option && option.value);
		selectedKeys = selectedKeys.filter(option => availableValues.includes(option.value)); // advanced cols from another site might not exist in some site, this can be caused by preset
		const selectedKeyWithoutInv = selectedKeys.filter((v) => v.group !== "INV_" && v.group !== "InvData");
		const selectedKeyWithInvNum = selectedKeys.filter((v) => v.group === "INV_");
		const selectedKeyWithInvData = selectedKeys.filter((v) => v.group === "InvData");
		
		// if all required inverter informations missing
		if((selectedKeyWithInvNum.length && !selectedKeyWithInvData.length) || (!selectedKeyWithInvNum.length && selectedKeyWithInvData.length)) {
			return null;
		}

		// with inverter information
		if(selectedKeyWithInvNum.length && selectedKeyWithInvData.length) {
			const inverterOptions = [];
			selectedKeyWithInvNum.forEach(invNum => {
				selectedKeyWithInvData.forEach(invData => {
					const invValue = invNum.value + invData.value;
					let invLabel = invValue.replaceAll(/_/g, ' ');
					if(invLabel.endsWith('Total'))   { invLabel += ' (kWh)' }
					if(invLabel.endsWith('Today'))   { invLabel += ' (kWh)' }
					if(invLabel.endsWith('Power'))   { invLabel += ' (kW)' }
					if(invLabel.endsWith('Control')) { invLabel += ' (%)' }
					inverterOptions.push({
						label: invLabel,
						value: invValue
					});
				});
			});
			return [...selectedKeyWithoutInv, ...inverterOptions];
		}
		
		// without inverter information
		return [...selectedKeyWithoutInv];

	}, [currentFieldSelection, selectedDataType, selectedPreset, colAdvancedContent]);

	const estimateCellCountBeforeFetch = useCallback((standardCols=[], advancedCols=[]) => {
		if(!selectedDataType) { return 0 }
		const dateRangeMsDiff = momentRangeRef.current.endDate.valueOf() - momentRangeRef.current.startDate.valueOf();
		let colCounts = [...standardCols, ...advancedCols].length;
		let siteCounts = selectedSites?.length || 0;
		let rowCounts = 1;
		if(selectedDataType.id === 1) { // P10
			rowCounts = Math.ceil(dateRangeMsDiff / (1000*60*10));
		}
		if(selectedDataType.id === 2 || selectedDataType.id === 4) { // Daily
			rowCounts = Math.ceil(dateRangeMsDiff / (1000*60*60*24));
		}
		if(selectedDataType.id === 3 || selectedDataType.id === 5) { // Monthly
			rowCounts = Math.ceil(dateRangeMsDiff / (1000*60*60*24*30));
		}
		if(selectedDataType.id === 7) { // Yearly
			rowCounts = Math.ceil(dateRangeMsDiff / (1000*60*60*24*365));
		}
		return colCounts * rowCounts * siteCounts;
	}, [ selectedDataType, selectedSites ]);

	const getURIForBuildingTable = useCallback(() => {
		if(!momentRangeRef.current.startDate || !momentRangeRef.current.endDate) { return }
		if (!selectedDataType || typeof(selectedDataType.id) !== 'number') { return };

		const checkedStandardCols = getStandardCols();
		const selectedAdvancedCols = getAdvancedCols(); // return Array if everything is good. return null if required inverter data is missing

		if(checkedStandardCols.length < 1) { return }
		if(selectedSitesRef.current.length < 1) { return }
		if(!selectedAdvancedCols) { return }

		let table_name = "";
		let colString = "";

		selectedAdvancedCols.sort(function (a, b) {
			if (a.value < b.value) return -1;
			if (a.value > b.value) return 1;
			return 0;
		});

		const prepCol = checkedStandardCols.concat(selectedAdvancedCols);

		switch (selectedDataType.id) {
			case 1:
				table_name = selectedSitesRef.current[0].table_name;
				colString = prepCol.map((v) => v.tableBelong ? v.tableBelong + "." + v.value : v.value).toString();
				break;

			case 2:
				table_name = "daily_calc";
				colString = checkedStandardCols
				.map((v) => (v.tableBelong + "." + v.value) + (v.alias ? `:${v.alias}` : '')) // add alias to column names
					.toString();
				break;

			case 3:
				table_name = "monthly_calc";
				colString = checkedStandardCols
					.map((v) => v.tableBelong + "." + v.value)
					.toString();
				break;

			case 7:
				table_name = "yearly_calc";
				colString = checkedStandardCols
					.map((v) => v.tableBelong + "." + v.value)
					.toString();
				break;

			case 6: // site_info
				table_name = "siteInfo";
				colString = checkedStandardCols
					.map((v) => (v.tableBelong + "." + v.value) + (v.alias ? `:${v.alias}` : '')) // add alias to column names
					.toString();
				break;

			case 8: // inverter_info
				table_name = "invertInfo"
				colString = checkedStandardCols
					.map((v) => v.tableBelong + "." + v.value)
					.toString();
				break;

			default:
				return;
		}

		const column = prepCol.map((v, i, a) => {
			let obj = {};
			a.length > 8
				? (obj = {
						title: v.label,
						field: v.alias || v.value,
						width: 150,
				  })
				: (obj = {
						title: v.label,
						field: v.alias || v.value,
				  });
			return obj;
		});

		let reqParam = {};

		if (isTimeRange && isDateRange)
			reqParam = {
				fromDate: formatDateTime(momentRangeRef.current.startDate),
				toDate: formatDateTime(momentRangeRef.current.endDate),
			};
		if (isMonthRange)
			reqParam = {
				fromDate: formatDate(momentRangeRef.current.startDate),
				toDate: formatDate(momentRangeRef.current.endDate),
			};
		if (isDateRange && !isTimeRange)
			reqParam = {
				fromDate: formatDate(momentRangeRef.current.startDate),
				toDate: formatDate(momentRangeRef.current.endDate),
			};
		if (isYearRange) {
			reqParam = {
				fromDate: formatYear(momentRangeRef.current.startDate),
				toDate: formatYear(momentRangeRef.current.endDate),
			};
		}

		const siteIDs = selectedSitesRef.current.map((v) => v.site_id);
		const clientIDs = selectedClient.map((v) => v.id);

		if (reqParam.length === 0) return;

		let uri;
		// calc uri
		if(selectedDataType.id === 1 || selectedDataType.id === 2 || selectedDataType.id === 3 || selectedDataType.id === 7) {
			uri = `/data_download/${table_name}/${reqParam.fromDate}/${reqParam.toDate}/${siteIDs}/${clientIDs}/${isGY}?cols=${colString}&country=${selectedSitesCountry}`;
		}
		// site info uri
		if(selectedDataType.id === 6) {
			uri = `/data_download/siteInfo?siteIDs=${siteIDs}&cols=${colString}&country=${selectedSitesCountry}`
		}
		// inverter uri
		if(selectedDataType.id === 8) {
			uri = `/data_download/inverterInfo/${clientIDs}/${siteIDs}/${isGY}?cols=${colString}&country=${selectedSitesCountry}`
		}

		if(table_name === 'monthly_calc' && isGY) { uri += '&isAutoAddOpStatus=1'; } // add operation_status if possible
		
		return { uri, table_name, column, checkedStandardCols, selectedAdvancedCols };		
	}, [selectedDataType, getStandardCols, getAdvancedCols, isTimeRange, isDateRange, isMonthRange, isYearRange, selectedClient, isGY, selectedSitesCountry]);

	const tableDataAPI = useMemo(() => {
		let uri;
		switch(selectedDataType?.id) {
			case 1: // P10
			case 2: // daily
			case 3: // monthly
			case 7: // yearly
				({ uri } = getURIForBuildingTable() || {});
				break;
			case 4:
				uri = getProductionUri('daily');
				break;
			case 5:
				uri = getProductionUri('monthly');
				break;
			case 6:
				({ uri } = getURIForBuildingTable() || {});
				break;
			case 8:
				({ uri } = getURIForBuildingTable() || {});
				break;
			default:
				break;
		}
		return uri;
	}, [getURIForBuildingTable, getProductionUri, selectedDataType]);

	const nonceProtect = useContext(NonceProtectContext);
	const downloadLink = useMemo(() => tableDataAPI && `${tableDataAPI}${tableDataAPI.includes('?') ? '&' : '?'}isDownloadFile=1&token=${window.localStorage.getItem('KC_JWT')}&nonce=${nonceProtect}`, [ tableDataAPI, nonceProtect ]);

	const updateTableCols = useCallback((column, records) => {
	/* if(selectedDataType?.id === 3 && (records?.[0]?.status_desc || records?.[0]?.power)) { // automatically add columns only for monthly
		let newColumn = column.map(col => colStandardContent.find(prettyCol => prettyCol.value === col.field));
		// if(records?.[0]?.power && !newColumn.find(col => col.value === 'power')) {
		// 	newColumn.push(colStandardContent.find(prettyCol => prettyCol.value === 'power'));
		// }
		if(records?.[0]?.status_desc && !newColumn.find(col => col.value === 'status_desc')) {
			newColumn.push(colStandardContent.find(prettyCol => prettyCol.value === 'status_desc'));
		}
		newColumn.sort((a, b) => a.ordering.localeCompare(b.ordering));
		debugger;
		newColumn = newColumn.map((prettyCol, i) => {
			console.log(prettyCol);
			return { field: prettyCol.value, title: prettyCol.label, width: newColumn.length > 8 ? 150 : undefined };
		});

		setTableCol(newColumn);
	} else {
		debugger;
		setTableCol(column);
	} */
		setTableCol(column);
	}, []);

	const transformTableData = useCallback((data, columns) => {
		if(!data?.length) { return }
		const cols = columns.map(column => column.field);
		const dateCol = selectedDataType?.id === 7 ? 'yearData' : 'dateData'; // yearly -> ID === 7
		const requiredCols = [dateCol, 'site_name', 'company'];
		if(!requiredCols.every(requiredCol => cols.includes(requiredCol)) || cols.length !== 4) { return }
		const numericalCol = cols.filter(col => !requiredCols.includes(col))[0];
		if(typeof(data[0][numericalCol]) !== 'number' && !(typeof(data[0][numericalCol]) === 'string' && /^([\d,])+(\.\d+)?$/gi.test(data[0][numericalCol]))) { return }
		const { newData, newColumns, dataColumnTitle } = oneNumericalColumnShift(data, 'site_name', dateCol, numericalCol, colStandardContent);
		return { newData, newColumns, dataColumnTitle };
	}, [ colStandardContent, selectedDataType ]);

	const buildTable = useCallback(() => {
		const {
			uri,
			checkedStandardCols,
			selectedAdvancedCols, // Array if everything is good. return null if required inverter information is missing
			column
		} = getURIForBuildingTable() || {};

		if( selectedDataType.id === 1 && !selectedAdvancedCols) {
			return Noti.create({ type: 'w', msg: 'Please select inverter number and inverter data' });
		}
		
		if(!uri) { return }

		const estimatedCellCounts = estimateCellCountBeforeFetch(checkedStandardCols, selectedAdvancedCols);
		if(estimatedCellCounts > maxDbTableCellCountsToFetch) {
			return Noti.create({ type: 'w', msg: 'Data you are trying to fetch is too big', title: 'Please reduce date range or de-select some columns collection and try again' })
		}
		
		setStandardCollapsed(true);
		setAdvancedCollapsed(true);
		setIsReset(false);
		setTableFoot([]);
		setTableData([]);
		setIsTable(true);
		setTableLoading(true);
		
		const freezedCol = checkedStandardCols.filter(({ readOnly }) => readOnly);

		GYAxios.get(uri).then(async (res) => {
			if (res.data.error) {
				console.error(res.data.error);
				turnOff('formTable');
				return Noti.create({ type: 'e', msg: 'Error occured!', title: 'Please contact IT' });
			}
			if(!res.data) {
				return Noti.create({ type: 'w', msg: 'No data to show' });
			}
			let data = res.data;
			try {
				data = await formatTableDataWorker({ data: data, dataTypeId: selectedDataTypeRef.current?.id });
			} catch (err) {
				console.error('Error while formatting:', err);
			}
			const { newData, newColumns, dataColumnTitle } = transformTableData(data, column) || {};
			if(newData && newColumns) {
				setFixedCols(1);
				setTableData(newData);
				updateTableCols(newColumns, data);
				setTableTitle(`${colStandardTitle}: ${dataColumnTitle}`);
			} else {
				setFixedCols({ left: freezedCol.length });
				setTableData(data);
				updateTableCols(column, data);
				setTableTitle(colStandardTitle);
			}
			setTableLoading(false);
		}).catch(err => {
			console.error('Error while fetching:', err);
			if(!err.response) {
				turnOff('formTable');
				return Noti.create({ type: 'e', msg: 'It takes too long to load the data', title: 'Please try reducing the range of date or reduce number of selected columns' });
			}
			return Noti.create({ type: 'e', msg: 'Error occured!', title: 'Please contact IT' });
		});
	}, [getURIForBuildingTable, selectedDataType, estimateCellCountBeforeFetch, transformTableData, formatTableDataWorker, updateTableCols, colStandardTitle]);

	useEffect(() => {
		const fetchUserAttr = () => {
			GYAxios.get(`/users/filter?column=keycloak_id&value=${kcID}`).then(
				(res) => {
					if (res.data.error !== undefined) {
						Noti.create({
							type: "e",
							msg: "Something is wrong. Please contact IT department!",
						});
						setUserAttr({});
						return;
					} else {
						if (Array.isArray(res.data) && res.data.length > 0) {
							setUserAttr(res.data[0]);
						} else {
							Noti.create({
								type: "e",
								msg: "Something is wrong. Please contact IT department!",
							});
							setUserAttr({});
							return;
						}
					}
				}
			);
		};
		fetchUserAttr();
	}, [kcID]);

	useEffect(() => {
		// wait for selectByOptions and userAttr are updated
		const timer = setTimeout(() => {
			if(isCountryListFetched.current) { return }
			if(!userAttr.country) { return }
			isCountryListFetched.current = true;
			const fetchCountryList = () => {
				setListCountry([]);
				setSelectedCountry({});

				let tempList = [];
				GYAxios.get("/select/countries").then((res) => {
					if (res.data.error !== undefined) {
						Noti.create({
							type: "e",
							msg: "Something is wrong. Please contact IT department!",
						});
						return;
					} else {
						if (Array.isArray(res.data) && res.data.length > 0) {
							tempList = res.data;
							if (userAttr.type === 1 && !user.roles.includes("_GY:External")) {
								setSelectedCountry(tempList[0].value);
								setListCountry(tempList);
							} else if (userAttr.type === 1 && user.roles.includes("_GY:External")) {
								const userCountry = userAttr?.country || 'TH';
								const countryList = tempList.filter((v) => v.value === userCountry);
								setListCountry(countryList);
								setSelectedCountry(countryList[0].value);

							} else {
								const siteCountries = clientOptions.filter(opt => opt.options?.length).map(opt => opt.options.map(inner_opt => inner_opt.value.country)).flat(1);
								const countrySet = [...new Set(siteCountries.length > 0 ? siteCountries : [ userAttr.country ])];
								const filteredCountry = tempList.filter((v) =>
									countrySet.includes(v.value)
								);
								setListCountry(filteredCountry);
								setSelectedCountry(filteredCountry[0].value);
							}
						} else {
							Noti.create({
								type: "e",
								msg: "Something is wrong. Please contact IT department!",
							});
							return;
						}
					}
				});
			};
			const fetchDataLabels = () => {
				GYAxios.get('/site/peak-labels').then((res) => {
					if(!res.data.length) { return Noti.create({ type: 'e', msg: 'Something is wrong. Please contact IT department!' }) }
					setOnOffLabels(res.data);
				});
			};
			const fetchBessPeakLabels = () => {
				GYAxios.get('/site/bess-peak-labels').then((res) => {
					if(!res.data.length) { return Noti.create({ type: 'e', msg: 'Something is wrong. Please contact IT department!' }) }
					const peakLabelRows = res.data;
					const peakLabels = peakLabelRows.reduce((acc, row) => ({ ...acc, [row.country]: row }), {});
					setBessLabels(peakLabels);
				});
			};
			fetchCountryList();
			fetchDataLabels();
			fetchBessPeakLabels();
		}, 10);
		return () => clearTimeout(timer);
	}, [userAttr, clientOptions]);

	// rebuild standard content when selectedSites is changed
	useEffect(() => {
		const country = selectedSitesCountry === 'ALL' ? 'TH' : selectedSitesCountry;
		let label = onOffLabels.find(label => label.country === country);
		if(!label) { return }
		if(!bessLabels[country]) { return }
		label = { labelON: label.label_1, labelOFF: label.label_2 };
		setLabelON(label.labelON);
		setLabelOFF(label.labelOFF);
		// rebuild standard content
		let dataLabelNames = filterOutHiddenDataLabelNames({ user, selectedSites, selectedCountry });
		dataLabelNames = filterOutChildDataLabelNames(dataLabelNames);
		let standardContent = dataLabelNames.filter(dataLabelName => dataLabelName.standard === 1);
		standardContent = replaceBessOnOffStdLabel(standardContent);
		standardContent = replaceOnOffLabel(standardContent, label);
		setColStandardContent(standardContent);
	}, [selectedSitesCountry, user, selectedSites, selectedCountry, filterColStandardContent, replaceBessOnOffStdLabel, replaceOnOffLabel, onOffLabels, bessLabels, filterOutHiddenDataLabelNames, filterOutChildDataLabelNames]);

	const autoSelectByForClient = useCallback(() => {
		const selectByOption = selectByOptions.find(option => option.value === 'client');
		onChangeSelectedBy(selectByOption);
		setIsSite(true);
	}, [onChangeSelectedBy, selectByOptions]);

	useEffect(() => {
		turnOff();
		if(!isGY) { // if GY, auto select "By Client" for client
			autoSelectByForClient();
		}
	}, [selectedCountry, isGY, autoSelectByForClient]);

	useEffect(() => {
		const callback = async ({ dataTypeId }) => {
			const dataType = listTypeRef.current.find(dataType => dataType.id === dataTypeId);
			if(!dataType) { // if list type is not loaded yet
				await delay(100);
				return callback({ dataTypeId });
			}
			if(selectedDataTypeRef.current?.id !== dataTypeId) { // only set data type if it doesn't match
				onChangeType(dataType);
			}
		};
		PresetEventEmitter.subscribe(presetEventNames.SET_SELECT_DATA_TYPE, callback); // emtited by preset select
		return () => PresetEventEmitter.unsubscribe(presetEventNames.SET_SELECT_DATA_TYPE, callback);
	}, [onChangeType]);

	useEffect(() => {
		const callback = async ({ contractIds }) => {
			const contractOptions = listContractRef.current.filter(contractOption => contractIds?.includes(contractOption.value));
			if(contractOptions.length !== contractIds?.length) { // if contracts is not loaded yet
				await delay(100);
				return callback({ contractIds });
			}
			if(selectedDataTypeRef.current?.id !== contractIds) { // only set data type if it doesn't match
				await onChangeClientContract(contractOptions);
			}
		};
		PresetEventEmitter.subscribe(presetEventNames.SET_SELECT_CONTRACTS, callback); // emtited by preset select
		return () => PresetEventEmitter.unsubscribe(presetEventNames.SET_SELECT_CONTRACTS, callback);
	}, [onChangeClientContract]);

	useEffect(() => {
		if(!listCountry?.length) { return }
		if(selectedPreset) { return }
		if(typeof(userAttr?.type) !== 'number') { return }
		if(isGY) { return }
		if(isSelectedSelectByForClient.current) { return }
		const timer = setTimeout(() => {
			isSelectedSelectByForClient.current = true;
			autoSelectByForClient();
		}, 500);
		return () => clearTimeout(timer);
	}, [ listCountry, userAttr, isGY, selectByOptions, onChangeSelectedBy, selectedPreset, selectedCountry, autoSelectByForClient ]);

	useEffect(() => {
		const callback = ({ value }) => {
			const getDataByOption = selectByOptions.find(option => option.value === value);
			if(!getDataByOption) { return }
			onChangeSelectedBy(getDataByOption);
		}
		PresetEventEmitter.subscribe(presetEventNames.SET_SELECT_GET_DATA_BY, callback); // emtited by preset select
		return () => PresetEventEmitter.unsubscribe(presetEventNames.SET_SELECT_GET_DATA_BY, callback);
	}, [ onChangeSelectedBy, selectByOptions ]);

	// like useEffect, but more information on which dependencies are changed
	useWatchStateChange((depIndex, prevValue, currentValue) => {
		if(!isReBuildTable) { return }
		if(!selectedSites?.length) { return }		
		if(!selectedDataType || typeof(selectedDataType.id) !== 'number') { return }

		// only forcus on changes on [momentRange] and [selectedDataType]
		if(depIndex > 1) { return }

		switch (selectedDataType.id) {
			case 1:
				buildTable();
				break;
			case 2:
				buildTable();
				break;
			case 3:
				buildTable();
				break;
			case 4:
				getProduction("daily");
				break;
			case 5:
				getProduction("monthly");
				break;
			// "Site Info" doesn't have date range
			// case 6:
			// 	getSiteInfo();
			// 	break;

			default:
				break;
		}
		setIsReBuildTable(false)
	}, [isReBuildTable, selectedDataType, buildTable, getProduction, getSiteInfo]);

	const columnCols = isContract ? 6 : 4;

	return listCountry.length > 0 ? (
		<div id="datadownload">
			<div className="panel-heading main-color-bg">
				<h3 className="panel-title headerDownload">
					{/* SELECT COUNTRY */}
					<span className="glyphicon glyphicon-cog" aria-hidden="true"></span>{" "}
					DOWNLOAD DATA OF{" "}
					{listCountry.length !== 0 && (
						<Select
							options={listCountry}
							defaultValue={listCountry[0]}
							onChange={onChangeCountry}
							styles={countryStyle}
						/>
					)}
				</h3>
				<Link to="/solar/documentation/download" className="btn-doc">
					<span className="glyphicon glyphicon-question-sign" aria-hidden="true"></span>
				</Link>
			</div>
			<Container fluid>
				<h3
					className="panel-title headerDownload"
					style={{ margin: "15px auto" }}
				>
					Site Collection
				</h3>
				
				<div>
					{/* SELECT PRESET */}
					<FormLabel popoverHelp={
						<>
							{(isEditPreset || isCreatePreset) && <div>
								By including sites in preset, next time when you use preset, those sites will be selected for you.
								<hr style={{ margin: '0.8em 0' }}  />
								By including date range in preset:
								<ul style={{ paddingLeft: '15px' }}>
									<li>If you select date range by clicking predefiend range like "Last 30 days", next time you use the preset, the range of the last 30 days will be selected for you.</li>
									<li>If you selecet date range by clicking specific dates, next time you use the preset, the range of those dates will be selected for you.</li>
								</ul>
							</div>}
							{(!isEditPreset && !isCreatePreset) && <div>
								{isITStaff && <>
									<span><StarBorder style={{fontSize: '1em', verticalAlign: 'middle'}} /> is default preset</span>
									<br/>
									<span><EmojiEmotionsOutlined style={{fontSize: '1em', verticalAlign: 'middle'}} /> is custom preset</span>
									<hr style={{margin: '0.8em 0'}} />
								</>}
								{!!(!selectedPresetOfType && currentFieldSelection?.[selectedDataType?.id]) && <>
									You can create custom preset by typing on it
									<hr style={{margin: '0.8em 0'}} />
								</>}
								If you can't select some presets, please try selecting some site(s) {!!isGY && 'or change "Select data by" and then try again'}
							</div>}
						</>
					}>Select Preset</FormLabel>
					<PresetForm
						selectedPreset={selectedPresetOfType}
						onPresetChange={handlePresetChange}
						currentFieldSelection={currentFieldSelection}
						resetCurrentFieldSelection={resetCurrentFieldSelection}
						selectedSelectBy={selectedSelectBy?.value}
						selectedContracts={selectedContracts?.map(contract => contract.contract_id)}
						selectedCountry={selectedCountry}
						dataType={selectedDataType?.id}
						isAllSelectSelected={isAllSelectSelected}
						isCreatePreset={isCreatePreset}
						isEditPreset={isEditPreset}
						editingPreset={editingPreset}
						setIsCreatePreset={setIsCreatePreset}
						setEditingPreset={setEditingPreset}
						duplicatePresetToFieldSelection={duplicatePresetToFieldSelection}
						selectedSites={selectedSites}
						defaultDateRange={{ timeRange, dateRange, monthRange, yearRange }}
						dateRangePickerRanges={dateRangePickerRanges}
						isITStaff={isITStaff}
					/>
				</div>
				
				<Row>
					{/* SELECT DATA BY */}
					<Col sm={columnCols} style={{ marginBottom: "1em", display: isGY ? 'block' : 'none' }}>
						<FormLabel popoverHelp={
							<div>You can select data by clients or contracts</div>
						}>Select data by</FormLabel>
						<Select
							value={selectedSelectBy}
							options={selectByOptions}
							placeholder="Select data "
							onChange={onChangeSelectedByWithClearPreset}
							styles={{ container: provided => ({ ...provided, margin: '0', width: '100%' }) }}
						/>
					</Col>

					{/* SELECT CONTRACT */}
					{isContract &&
					<Col sm={columnCols} style={{ marginBottom: "1em" }}>
						<FormLabel>Select contract(s)</FormLabel>
						<Select
							value={selectedContracts}
							options={listContract}
							placeholder="SELECT CONTRACT(S)"
							onChange={onChangeClientContractWithClearPreset}
							styles={{ container: provided => ({ ...provided, margin: '0', width: '100%' }) }}
							isMulti={true}
						/>
					</Col>}

					{/* SELECT SITES */}
					{isSite && 
					<Col sm={columnCols} style={{ marginBottom: "1em" }}>
						<FormLabel popoverHelp={
							!!isGY && <div>You can also selecet all sites of a client by clicking on the client name</div>
						}>Select site(s)</FormLabel>
						<SitesSelect
							value={selectedSites}
							onChange={handleSitesSelectChange}
							filterClient={siteSelectFilterByClient}
							filterSite={siteSelectFilterBySite}
							filterCountry={siteSelectFilterByCountry}
						/>
					</Col>}

					{/* SELECT DATA TYPE */}
					{isDataType &&
					<Col sm={columnCols} style={{ marginBottom: "1em" }}>
						<FormLabel popoverHelp={
							selectedSites?.length >= 2 && <div>If you can't select P10 data, it means you selected 2 or more sites. Please try selecting only 1 site and try again</div>
						}>Select data type</FormLabel>
						<Select
							value={selectedDataType}
							options={listType}
							placeholder="SELECT DATA TYPE"
							onChange={onChangeTypeWithDispatch}
							styles={{ container: provided => ({ ...provided, margin: '0', width: '100%' }) }}
							isDisabled={isListTypeLoading || !selectedSites?.length}
						/>
					</Col>}
				</Row>

				{/* STANDARD FIELDS */}
				{isStandard && (
					<Row style={{ margin: "0 auto 1em" }}>
						<StandardCol
							content={colStandardContent}
							loading={() => loading(false)}
							needReset={isReset}
							isCollapsed={isStandardCollapsed}
							setCollapsed={setStandardCollapsed}
							onInputChange={handleCurrentFieldSelectionChange}
							selectedPreset={selectedPreset}
							clearPreset={clearPreset}
							currentFieldSelection={currentFieldSelection}
							selectedDataType={selectedDataType?.value}
							isEditPreset={isEditPreset}
						/>
					</Row>
				)}

				{/* ADAVANCED FIELDS */}
				{isAdvanced && (
					<Row style={{ margin: "0 auto 1em" }}>
						<AdvancedCol
							isCollapsed={isAdvancedCollapsed}
							setCollapsed={setAdvancedCollapsed}
							content={colAdvancedContent}
							clearPreset={clearPreset}
							needReset={isReset}
							onInputChange={handleCurrentFieldSelectionChange}
							selectedPreset={selectedPreset}
							currentFieldSelection={currentFieldSelection}
							selectedDataType={selectedDataType?.value}
							isEditPreset={isEditPreset}
						/>
					</Row>
				)}

				{isDateRange && (
					<Row style={{ maxWidth: '410px', margin: "0 auto 1em" }}>
						<DateRangePicker
							key={dateRangePickerKey}
							maxDate={moment()}
							ranges={dateRangePickerRanges}
							alwaysShowCalendars
							timePicker={isTimeRange ? true : false}
							onApply={handleDateTimeMonthRangePickerChange}
							autoApply={isTimeRange ? false : true}
						>
							<Form>
								<Form.Group controlId="downloadDRP">
									<Form.Control
										type="text"
										placeholder="Select a date range"
										readOnly
										value={isTimeRange ? `${displayDateTime(momentRange.startDate)} - ${displayDateTime(momentRange.endDate)}` : `${displayDate(momentRange.startDate)} - ${displayDate(momentRange.endDate)}`}
										style={{
											textAlign: "center",
											backgroundColor: "white",
										}}
									/>
								</Form.Group>
							</Form>
						</DateRangePicker>
					</Row>
				)}

				{(isMonthRange || isYearRange) && (
					<Row style={{ maxWidth: '300px', margin: "0 auto 1em" }} id="datadownload">
						<DatePicker
							selectsRange={true}
							selected={momentRange.startDate && localTimeToPickerTime(momentRange.startDate.toDate())}
							startDate={momentRange.startDate && localTimeToPickerTime(momentRange.startDate.toDate())}
							endDate={momentRange.endDate && localTimeToPickerTime(momentRange.endDate.toDate())}
							onChange={handleDateTimeMonthRangePickerChange}
							showMonthYearPicker={isMonthRange}
							showYearPicker={isYearRange}
							maxDate={localTimeToPickerTime(todayDate)}
							dateFormat={isMonthRange ? "MM/yyyy" : "yyyy"}
						/>
					</Row>
				)}

				 {/* SHOW TABLE BUTTON */}
				{isStandard && (
					<div style={{ margin: '0 auto 1em', display: 'flex', justifyContent: 'center', gap: '1em' }}>
							{downloadLink && (
								<button className="btn btn-primary">
									<a href={downloadLink} style={{ color: 'white' }}>
										Download
									</a>
								</button>
							)}
							<button
								className="btn btn-success"
								onClick={(e) => buildTable()}
								disabled={!selectedDataType || !selectedSites.length}
							>
								Show Table
							</button>
							<button
								className="btn btn-info"
								onClick={(e) => {
									setIsReset(true);
									resetCurrentFieldSelection();
									clearPreset();
								}}
							>
								Reset Info Collection
							</button>
					</div>
				)}

				{/* TABLE */}
				{isTable && (
					<Row style={{ margin: "15px auto" }}>
						<DownloadableTable
							// key prop is required to fix library UI bug when tableData changes
							key={tableDataAPI}
							title={tableTitle}
							isLoading={isTableLoading}
							cols={tableCol}
							tableData={tableData}
							tableFoot={tableFoot}
							fileName={tableFilename}
							fixedCols={fixedCols}
						/>
					</Row>
				)}
			</Container>

			<Noti />

			{loader && (
				<Row style={{ marginTop: "15px", marginBottom: "15px" }}>
					<div className="overlay translucent" style={showLoader}>
						<Loader
							translucent={true}
							customStyle={{
								width: "150px",
								height: "150px",
								position: "fixed",
								left: "50%",
								top: "35%",
							}}
						/>
					</div>
				</Row>
			)}
		</div>
	) : (
		<LoaderGraph style={{ height: "37vh" }} />
	);
};
